// oxlint-disable jsx-no-new-object-as-prop

import { Link, useLocation, useMatchRoute, useSearch } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import type { Transition } from "motion/react";
import { AnimatePresence, m } from "motion/react";
import { useCallback, useMemo } from "react";

import { useGlobal } from "../lib/global-context";
import { loadStore, menuOpenStore } from "../lib/store";
import type { VoidComponent } from "../lib/types";
import { booleanAttribute, imageUrlBuilder } from "../lib/utils";
import { transitionAnimate, transitionExit } from "./about/const";
import { Icon } from "./icon";
import { SoundToggleButton } from "./sound-toggle-button";

const MobileNavigation: VoidComponent = () => {
	const isMenuOpen = useStore(menuOpenStore);
	const { pillars, aboutLinkText, foundationLink, foundationTitle } = useGlobal();
	const search = useSearch({ from: "__root__" });
	const matchRoute = useMatchRoute();
	const location = useLocation();
	const isStarted = useStore(loadStore, (state) => state.isStarted);
	const aboutSearch = { about: 1 } as const;

	const handleToggleMenu = useCallback(() => {
		menuOpenStore.setState((prev) => !prev);
	}, []);

	const handleCloseMenu = useCallback(() => {
		menuOpenStore.setState(false);
	}, []);

	const isIndexPage = matchRoute({ to: "/" });

	const pillarParams = useMemo(
		() => new Map(pillars.map((p) => [p._key, { pillar: p.slug.current }])),
		[pillars],
	);

	if (!isStarted) {
		return null;
	}

	const headerTransition = {
		type: "spring",
		visualDuration: 0.8,
		bounce: 0,
		restDelta: 0.001,
	} as const satisfies Transition;

	return (
		<header className="fixed inset-x-0 top-0 z-130 block desktop:hidden">
			<AnimatePresence>
				{isMenuOpen && (
					<div className="fixed inset-0 z-100 flex flex-col pt-19">
						<m.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1, transition: transitionAnimate }}
							exit={{ opacity: 0, transition: transitionExit }}
							className="absolute inset-0 -z-1 bg-surface-primary"
						/>

						<div className="flex flex-1 flex-col justify-between p-4">
							<ul className="flex flex-col gap-1">
								{pillars.map((pillar, index) => {
									const pillarParam = pillarParams.get(pillar._key);
									const isActive = location.pathname.startsWith(`/${pillar.slug.current}`);

									return (
										<m.li
											initial={{ opacity: 0, y: "1rem" }}
											animate={{
												opacity: 1,
												y: "0",
												transition: { ...transitionAnimate, delay: index * 0.05 },
											}}
											exit={{
												opacity: 0,
												y: "0.5rem",
												transition: { ...transitionExit, delay: index * 0.02 },
											}}
											key={pillar._key}
										>
											{pillarParam ? (
												<Link
													to="/$pillar"
													params={pillarParam}
													onClick={handleCloseMenu}
													className="flex min-h-16 items-center gap-4 bg-white px-4 text-foreground hover:bg-accent-pressed hover:text-surface-primary data-active:bg-accent data-active:text-surface-primary"
													data-active={booleanAttribute(isActive)}
												>
													<img
														src={imageUrlBuilder(pillar.thumbnail)
															.width(48)
															.auto("format")
															.dpr(Math.min(window.devicePixelRatio, 2))
															.url()}
														alt={pillar.title}
														className="size-12"
													/>

													<span className="flex-1 text-sm leading-5 transition-colors duration-200">
														{pillar.title}
													</span>
												</Link>
											) : null}
										</m.li>
									);
								})}
							</ul>

							<div className="flex flex-col gap-1">
								<m.a
									initial={{ opacity: 0, y: "1rem" }}
									animate={{ opacity: 1, y: "0", transition: { ...transitionAnimate, delay: 0.2 } }}
									exit={{ opacity: 0, y: "0.5rem", transition: { ...transitionExit, delay: 0.05 } }}
									type="button"
									href={foundationLink}
									target="_blank"
									rel="noopener noreferrer"
									className="flex h-16 items-center justify-center gap-1 bg-white text-sm transition-colors duration-200 hover:bg-accent-pressed hover:text-surface-primary active:bg-accent active:text-surface-primary"
								>
									<span>{foundationTitle}</span>

									<Icon name="arrow-up-right" className="size-5" />
								</m.a>

								<m.div
									initial={{ opacity: 0, y: "1rem" }}
									animate={{
										opacity: 1,
										y: "0",
										transition: { ...transitionAnimate, delay: 0.25 },
									}}
									exit={{ opacity: 0, y: "0.5rem", transition: { ...transitionExit, delay: 0.07 } }}
								>
									<Link
										to="."
										search={search?.about === 1 ? undefined : aboutSearch}
										onClick={handleCloseMenu}
										className="group flex h-16 items-center justify-center bg-white text-sm transition-colors duration-200 hover:bg-accent-pressed hover:text-surface-primary active:bg-accent data-active:bg-accent data-active:text-surface-primary"
										data-active={booleanAttribute(search?.about === 1)}
									>
										{aboutLinkText}
									</Link>
								</m.div>
							</div>
						</div>
					</div>
				)}
			</AnimatePresence>

			<AnimatePresence>
				<nav
					className="group relative z-210 flex items-center justify-between px-4 pt-4"
					data-open={booleanAttribute(isMenuOpen)}
				>
					{isIndexPage ? (
						<div className="pointer-events-none h-12 w-17" />
					) : (
						<m.div
							initial={{ opacity: 0, y: "0.5rem" }}
							animate={{ opacity: 1, y: "0" }}
							exit={{ opacity: 0, y: "0.5rem" }}
							transition={headerTransition}
						>
							<Link
								to="/"
								className="group/house flex h-12 w-17 items-center justify-center bg-surface-primary transition-colors duration-200 group-data-open:bg-white hover:bg-accent-pressed active:bg-accent"
								onClick={handleCloseMenu}
							>
								<Icon
									name="home"
									className="size-5 text-foreground transition-colors duration-200 group-hover/house:text-surface-primary group-active/house:text-surface-primary"
								/>
							</Link>
						</m.div>
					)}

					<m.div
						initial={{ opacity: 0, y: "0.5rem" }}
						animate={{ opacity: 1, y: "0" }}
						exit={{ opacity: 0, y: "0.5rem" }}
						transition={headerTransition}
						className="flex items-center gap-0.5"
					>
						<SoundToggleButton />

						<button
							type="button"
							onClick={handleToggleMenu}
							className="flex h-12 w-17 cursor-pointer items-center justify-center bg-surface-primary transition-colors duration-200 ease-out-quart group-data-open:bg-white"
							aria-label={isMenuOpen ? "Close menu" : "Open menu"}
							aria-expanded={isMenuOpen}
						>
							<div
								className="group relative flex size-5 items-center justify-center"
								data-open={booleanAttribute(isMenuOpen)}
							>
								<svg
									viewBox="0 0 24 24"
									className="size-5 shrink-0 text-foreground"
									fill="none"
									strokeWidth="1.5"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									{/* oxlint-disable-next-line react/jsx-no-literals */}
									<title>Menu</title>

									<line
										className="origin-center translate-y-[-7px] transition-transform duration-300 ease-out-quart group-data-open:translate-y-0 group-data-open:rotate-45"
										x1="3"
										y1="12"
										x2="21"
										y2="12"
									/>

									<line
										className="origin-center transition-[transform,opacity] duration-300 ease-out-quart group-data-open:scale-x-0 group-data-open:opacity-0"
										x1="3"
										y1="12"
										x2="21"
										y2="12"
									/>

									<line
										className="origin-center translate-y-[7px] transition-transform duration-300 ease-out-quart group-data-open:translate-y-0 group-data-open:-rotate-45"
										x1="3"
										y1="12"
										x2="21"
										y2="12"
									/>
								</svg>
							</div>
						</button>
					</m.div>
				</nav>
			</AnimatePresence>
		</header>
	);
};

export { MobileNavigation };
